/**
 * Loads a third-party script once, however many callers ask for it.
 *
 * The wallets are the only thing in this app that reaches outside our own origin at runtime, and both SDKs
 * are wanted from two places — the capability probe and the sheet itself. Caching the promise rather than the
 * script element means a second caller during the first load waits for it instead of injecting a duplicate.
 *
 * A failure rejects rather than hanging. A wallet whose SDK never arrives has to become "this method is not
 * available" quickly, because the alternative is a guest tapping a button that does nothing while their table
 * waits.
 */
const loading = new Map<string, Promise<void>>();

export function loadScript(src: string): Promise<void> {
    const existing = loading.get(src);
    if (existing) return existing;

    const promise = new Promise<void>((resolve, reject) => {
        if (typeof document === 'undefined') {
            reject(new Error('No document to load a script into.'));
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            // Forget the failure so a later attempt can retry rather than replaying a rejected promise
            // forever — a guest on flaky restaurant wifi deserves a second chance at it.
            loading.delete(src);
            reject(new Error(`Failed to load ${src}`));
        };

        document.head.append(script);
    });

    loading.set(src, promise);
    return promise;
}

/** Testing seam: forgets what has been loaded so a case can start from nothing. */
export function resetLoadedScripts(): void {
    loading.clear();
}
